import ProphetsCosmicBackground from "./ProphetsCosmicBackground";

/**
 * Prophets Hero — native cinematic composition.
 *
 * Layer order (bottom → top), all scoped inside this component:
 *   1. deep black base
 *   2. subtle atmospheric radial glow
 *   3. faint noise / vignette
 *   4. large outer orbit (very slow drift)
 *   5. concentric orbital rings + partial arcs
 *   6. faint stars + a few accent nodes
 *   7. soft central glow behind the logo lockup
 *   8. official Prophets symbol + wordmark (single transparent asset)
 *   9. tagline
 *  10. CTA buttons
 *  11. small supporting line + scroll indicator
 *
 * The background is built with SVG + CSS gradients — no flattened mockup.
 * The logo remains a separate responsive element (never baked in).
 */
export function ProphetsHero() {
  return (
    <section
      id="top"
      data-testid="prophets-hero"
      className="prophets-hero relative w-full min-h-[100svh] flex items-center justify-center overflow-hidden"
    >
      {/* 1. Deep black base + 2. atmospheric radial + 3. top-fade for header legibility */}
      <div aria-hidden="true" className="prophets-hero-base" />
      <div aria-hidden="true" className="prophets-hero-glow" />
      <div aria-hidden="true" className="prophets-hero-topfade" />

      {/* 4-6. Native orbital composition (SVG, symmetric, centered) */}
      <svg
        aria-hidden="true"
        viewBox="0 0 1600 1000"
        preserveAspectRatio="xMidYMid slice"
        className="prophets-hero-svg absolute inset-0 w-full h-full z-0 pointer-events-none"
      >
        <defs>
          {/* Soft radial glow behind logo */}
          <radialGradient id="hero-center-glow" cx="50%" cy="50%" r="34%">
            <stop offset="0%"  stopColor="#8a94b4" stopOpacity="0.20" />
            <stop offset="45%" stopColor="#3a4160" stopOpacity="0.08" />
            <stop offset="100%" stopColor="#000000" stopOpacity="0" />
          </radialGradient>
          {/* Fading orbit stroke — brighter at the sides, transparent at the poles */}
          <linearGradient id="hero-orbit-stroke" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%"   stopColor="rgba(220,228,244,0)"    />
            <stop offset="18%"  stopColor="rgba(220,228,244,0.12)" />
            <stop offset="50%"  stopColor="rgba(220,228,244,0.24)" />
            <stop offset="82%"  stopColor="rgba(220,228,244,0.12)" />
            <stop offset="100%" stopColor="rgba(220,228,244,0)"    />
          </linearGradient>
          {/* Restrained violet accent for a single arc */}
          <linearGradient id="hero-orbit-violet" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%"   stopColor="rgba(150,140,220,0)"    />
            <stop offset="50%"  stopColor="rgba(160,150,220,0.18)" />
            <stop offset="100%" stopColor="rgba(150,140,220,0)"    />
          </linearGradient>
          <linearGradient id="hero-orbit-blue" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%"   stopColor="rgba(120,150,220,0)"    />
            <stop offset="50%"  stopColor="rgba(140,170,230,0.14)" />
            <stop offset="100%" stopColor="rgba(120,150,220,0)"    />
          </linearGradient>
        </defs>

        {/* Central soft glow */}
        <rect x="0" y="0" width="1600" height="1000" fill="url(#hero-center-glow)" opacity="0.65" />

        {/* Concentric orbit rings — all centered on (800, 500) */}
        <g className="prophets-hero-orbits">
          <ellipse cx="800" cy="500" rx="720" ry="200"
                   fill="none" stroke="url(#hero-orbit-stroke)" strokeWidth="0.8" />
          <ellipse cx="800" cy="500" rx="620" ry="170"
                   fill="none" stroke="url(#hero-orbit-stroke)" strokeWidth="0.7" opacity="0.9" />
          <ellipse cx="800" cy="500" rx="520" ry="145"
                   fill="none" stroke="url(#hero-orbit-stroke)" strokeWidth="0.65" opacity="0.85" />
          <ellipse cx="800" cy="500" rx="420" ry="120"
                   fill="none" stroke="url(#hero-orbit-blue)"   strokeWidth="0.6"  opacity="0.9" />
          <ellipse cx="800" cy="500" rx="320" ry="90"
                   fill="none" stroke="url(#hero-orbit-violet)" strokeWidth="0.55" opacity="0.85" />
        </g>

        {/* Two subtle circular guides */}
        <g opacity="0.35">
          <circle cx="800" cy="500" r="260"
                  fill="none" stroke="rgba(220,228,244,0.14)" strokeWidth="0.6" />
          <circle cx="800" cy="500" r="200"
                  fill="none" stroke="rgba(220,228,244,0.09)" strokeWidth="0.5" strokeDasharray="2 6" />
        </g>

        {/* Slow-drift outer orbit (only one animates, restrained) */}
        <g className="prophets-hero-orbit-drift">
          <ellipse cx="800" cy="500" rx="780" ry="220"
                   fill="none" stroke="rgba(220,228,244,0.10)" strokeWidth="0.55"
                   strokeDasharray="1 8" />
        </g>

        {/* Faint stars — hand-placed deterministic points */}
        <g fill="#ffffff" opacity="0.75">
          {STARS.map(([cx, cy, r, o]) => (
            <circle key={`s-${cx}-${cy}`} cx={cx} cy={cy} r={r} opacity={o} />
          ))}
        </g>

        {/* Four soft accent nodes along the widest orbit — very subtle */}
        <g>
          <circle cx="80"   cy="500" r="2.2" fill="#c9d1e0" opacity="0.6" />
          <circle cx="1520" cy="500" r="2.2" fill="#c9d1e0" opacity="0.6" />
          <circle cx="80"   cy="500" r="9"   fill="none" stroke="rgba(220,228,244,0.11)" strokeWidth="0.5" />
          <circle cx="1520" cy="500" r="9"   fill="none" stroke="rgba(220,228,244,0.11)" strokeWidth="0.5" />
        </g>
      </svg>

      {/* Animated cosmic layer — sits above the static SVG and below hero
          content. To restore the exact previous static hero, remove this
          line OR set ENABLE_COSMIC_BG = false in the component. */}
      <ProphetsCosmicBackground />

      {/* 7-11. Hero content lockup — SIMPLIFIED. Only the metallic P +
          PROPHETS wordmark asset remain. The tagline, MINT + BUY ON
          MARKETPLACE buttons, and "10 families" line were removed here
          on purpose (the marketplace CTAs still exist further down the
          page and in the top navigation). Vertical centering is handled
          entirely by the section's `flex items-center justify-center`
          + `min-h-[100svh]`. */}
      <div className="relative z-10 flex flex-col items-center text-center px-5 md:px-10 max-w-[1100px] mx-auto w-full">
        {/* Soft central glow ONLY behind the logo — restrained */}
        <span aria-hidden="true" className="prophets-hero-center-halo" />

        {/* Official transparent logo lockup (symbol + wordmark preserved as one asset) */}
        <img
          src="/img/prophets_logo.webp"
          alt="Prophets — 1,111 visionaries"
          data-testid="hero-logo"
          width="1697"
          height="2000"
          fetchPriority="high"
          decoding="async"
          className="p-logo-in relative w-[240px] sm:w-[300px] md:w-[360px] lg:w-[400px] h-auto select-none"
          draggable={false}
        />
      </div>

      {/* Scroll indicator */}
      <div
        aria-hidden="true"
        data-testid="hero-scroll-indicator"
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 p-fade-in"
        style={{ animationDelay: "1600ms" }}
      >
        <span className="p-mono text-[9px] tracking-[0.4em] uppercase text-white/45">
          Scroll
        </span>
        <span className="relative block w-px h-10 bg-gradient-to-b from-white/40 to-transparent">
          <span className="p-scroll-bob absolute -left-[3px] top-0 block w-[7px] h-[7px] rounded-full bg-white/85 shadow-[0_0_8px_rgba(200,210,230,0.6)]" />
        </span>
      </div>
    </section>
  );
}

/* Deterministic star points — [cx, cy, r, opacity] in the 1600×1000 viewBox.
   Chosen to sit outside the central logo area and avoid the CTA row. */
const STARS = [
  [ 120, 140, 0.9, 0.55], [ 260,  90, 0.7, 0.35], [ 380, 220, 1.1, 0.75],
  [ 540, 120, 0.6, 0.30], [ 720,  80, 0.8, 0.45], [ 880,  60, 0.9, 0.55],
  [1040,  90, 0.6, 0.30], [1180, 160, 1.0, 0.65], [1320, 110, 0.7, 0.40],
  [1460, 200, 0.9, 0.50],
  [  90, 780, 0.9, 0.45], [ 220, 880, 0.6, 0.30], [ 380, 820, 1.0, 0.55],
  [ 560, 900, 0.7, 0.35], [ 740, 860, 0.9, 0.50], [ 920, 920, 0.7, 0.35],
  [1080, 830, 1.0, 0.55], [1260, 890, 0.6, 0.30], [1420, 800, 0.9, 0.50],
  [1500, 870, 0.7, 0.35],
  [ 200, 460, 0.6, 0.30], [1400, 540, 0.6, 0.30],
];
